/**
 * Settings ▸ Repository roles ▸ CI wiring — what "Publish to CI" does.
 *
 * Picker-less since guided-repo-setup Phase B: the fitness repo is chosen on
 * the Fitness tests capability row above (DocReposTab), which keeps the
 * guardrails CI target in lockstep. This section keeps the operational
 * details — what lands in the PR and the App permission it needs — plus the
 * currently configured target.
 */

import { useState, useEffect } from 'react';
import { CheckCircle, GitPullRequest } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { guardrailsApi, type CiSettings } from '@/services/guardrailsApi';

export default function CiIntegrationTab() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<CiSettings | null>(null);

  useEffect(() => {
    guardrailsApi
      .getCiSettings()
      .then(setSettings)
      .catch(() => setSettings(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <LoadingState size="md" />;
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <GitPullRequest className="w-5 h-5 text-slate-500" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            CI integration
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            How fitness tests reach your repository. The target repo is the{' '}
            <strong>Fitness tests</strong> role above.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-3">
        <p className="text-xs text-slate-500">
          When you click "Publish to CI" on the Checks page, a PR is created on the fitness
          tests repository with the generated{' '}
          <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">check_*.sh</code> scripts,
          a <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">scripts/run_all.sh</code>{' '}
          runner, and a self-contained{' '}
          <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">.github/workflows/systemdox-checks.yml</code>{' '}
          that runs them on every pull request. Committing the workflow needs the GitHub App's{' '}
          <strong>Workflows</strong> permission.
        </p>

        {settings?.configured && settings.owner && settings.repo ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
            <CheckCircle className="w-4 h-4" />
            Publishes to <strong>{settings.owner}/{settings.repo}</strong>
          </div>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No target yet — set the Fitness tests role above and save.
          </p>
        )}
      </div>
    </div>
  );
}
